import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { formatApiErrorMessage } from '../../api/apiErrorMessage'
import {
  updateRestaurantPrivateSupplier,
  type RestaurantPrivateSupplierDto,
} from '../../api/restaurantMyPrices'
import { normalizeOgrnDigits, ogrnLabel, ogrnValidationMessage } from '../../utils/ogrn'

/**
 * Карточка поставщика из «Моих прайсов» — окном по центру страницы.
 *
 * Реквизиты, по которым карточка сопоставляется со следующим прайс-листом, показываются,
 * но не правятся: юридическое название и регистрационный номер приходят из самого файла,
 * и правка развела бы карточку с ним. ИНН — исключение: он необязателен, поставщик
 * определяется по ОГРН, а ИНН нужен, когда доходит до расчётов и документов.
 *
 * Контакты менеджера — персональные данные представителя контрагента. Вносит их Клиент,
 * оператором в их отношении выступает он же; на сервере они хранятся зашифрованными.
 */
export const SupplierCardModal = ({
  supplier,
  onDismiss,
  onSaved,
}: {
  supplier: RestaurantPrivateSupplierDto
  onDismiss: () => void
  onSaved: (updated: RestaurantPrivateSupplierDto) => void
}) => {
  const navigate = useNavigate()
  const dialogRef = useRef<HTMLDivElement>(null)

  const [legalName, setLegalName] = useState(supplier.legalName)
  const [ogrn, setOgrn] = useState(supplier.ogrn ?? '')
  const [amount, setAmount] = useState(
    supplier.minOrderAmount != null ? String(supplier.minOrderAmount) : '',
  )
  const [unknown, setUnknown] = useState(supplier.minOrderAmountUnknown)
  const [inn, setInn] = useState(supplier.inn ?? '')
  const [managerName, setManagerName] = useState(supplier.managerName ?? '')
  const [managerPhone, setManagerPhone] = useState(supplier.managerPhone ?? '')
  const [managerEmail, setManagerEmail] = useState(supplier.managerEmail ?? '')

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // onDismiss приходит из родителя новой функцией на каждый его рендер. Держим последнюю
  // в ref, чтобы эффект ниже не перезапускался: его очистка возвращает фокус, и при
  // перезапуске она выдёргивала бы фокус из окна посреди работы.
  const onDismissRef = useRef(onDismiss)
  useEffect(() => {
    onDismissRef.current = onDismiss
  })

  useEffect(() => {
    // Запоминаем, откуда открыли, и возвращаем фокус туда при закрытии. Без этого фокус
    // уходил в body, и человек с клавиатуры терял место в таблице — заново проходил Tab-ом
    // всё боковое меню, чтобы добраться до следующего поставщика.
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    dialogRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismissRef.current()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      if (opener && opener.isConnected) opener.focus()
    }
  }, [])

  const parsedAmount = amount.trim() === '' ? null : Number(amount.trim().replace(',', '.'))
  const amountInvalid =
    !unknown && parsedAmount != null && (!Number.isFinite(parsedAmount) || parsedAmount < 0)
  const innInvalid = inn.trim() !== '' && !/^\d{10}(\d{2})?$/.test(inn.trim())
  const nameInvalid = legalName.trim() === ''
  // Сравниваем цифры, а не строки: пробел или дефис при вводе номер не меняют, и хинт
  // про сопоставление в таком случае только пугал бы.
  const ogrnChanged = normalizeOgrnDigits(ogrn) !== normalizeOgrnDigits(supplier.ogrn ?? '')
  // Проверяем только изменённый номер. Если проверять сохранённый, карточка с номером,
  // заведённым до проверки контрольной цифры, открывалась бы с ошибкой и не давала бы
  // сохранить вообще ничего — даже минимальную сумму. Неизменный номер на сервер и не
  // уходит. false — поле может быть пустым: у карточек до перехода на ОГРН номера нет.
  const ogrnError = ogrnChanged ? ogrnValidationMessage(ogrn, false) : null

  const handleSave = async () => {
    if (amountInvalid || innInvalid || nameInvalid || ogrnError) {
      setError('Проверьте выделенные поля')
      return
    }
    setSaving(true)
    setError(null)
    try {
      onSaved(
        await updateRestaurantPrivateSupplier(supplier.id, {
          legalName: legalName.trim(),
          // Шлём только изменённый номер: неизменный незачем перепроверять на сервере.
          ...(ogrnChanged ? { ogrn: normalizeOgrnDigits(ogrn) } : {}),
          minOrderAmount: parsedAmount,
          minOrderAmountUnknown: unknown,
          inn: inn.trim() === '' ? null : inn.trim(),
          managerName: managerName.trim() === '' ? null : managerName.trim(),
          managerPhone: managerPhone.trim() === '' ? null : managerPhone.trim(),
          managerEmail: managerEmail.trim() === '' ? null : managerEmail.trim(),
        }),
      )
    } catch (caught) {
      setError(formatApiErrorMessage(caught, 'Не удалось сохранить карточку'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="market-paywall-overlay" role="presentation" onClick={onDismiss}>
      <div
        ref={dialogRef}
        className="market-paywall-modal market-supplier-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="supplier-card-title"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="market-supplier-card__head">
          <div>
            <h2 id="supplier-card-title" className="market-paywall-modal__title">
              {supplier.legalName}
            </h2>
            {/*
              Плашка на всю ширину ради одного номера занимала строку и отступы вокруг
              неё. Номер — идентификатор, а не поле формы, ему место в подписи.
            */}
            <label className="market-supplier-card__ogrn">
              <span className="market-supplier-card__ogrn-label">
                {normalizeOgrnDigits(ogrn) ? ogrnLabel(normalizeOgrnDigits(ogrn)) : 'ОГРН'}
              </span>
              {/*
                Поле, оформленное как текст подписи: номер правится там же, где читается,
                без отдельной строки формы. Подчёркивание при наведении и фокусе
                показывает, что это поле.
              */}
              <input
                className={`market-supplier-card__ogrn-input${ogrnError ? ' is-invalid' : ''}`}
                inputMode="numeric"
                value={ogrn}
                placeholder="не указан"
                aria-invalid={ogrnError ? true : undefined}
                aria-describedby={ogrnError || ogrnChanged ? 'supplier-card-ogrn-hint' : undefined}
                onChange={(event) => setOgrn(event.target.value)}
              />
            </label>
          </div>
          <div className="market-supplier-card__actions">
            <button
              type="button"
              className="echat-btn echat-btn--ghost echat-btn--sm"
              onClick={() => navigate('/my-prices')}
              disabled={saving}
            >
              Открыть прайс
            </button>
            <button
              type="button"
              className="echat-btn echat-btn--ghost echat-btn--sm"
              onClick={() => navigate(`/my-prices/import?supplier=${supplier.id}`)}
              disabled={saving}
            >
              Обновить прайс
            </button>
          </div>
        </div>

        {/*
          Хинт и ошибка — на всю ширину под шапкой, а не в колонке заголовка: там рядом стоит
          кнопка, и текст ломался на четыре строки, делая карточку выше, чем до переделки.
        */}
        {ogrnError ? (
          <p id="supplier-card-ogrn-hint" className="echat-field__error market-supplier-card__ogrn-hint" role="alert">
            {ogrnError}
          </p>
        ) : ogrnChanged ? (
          <p id="supplier-card-ogrn-hint" className="market-supplier-card__ogrn-hint">
            После изменения ОГРН следующий прайс может не сопоставиться автоматически —
            обновите его кнопкой «Обновить прайс» выше, иначе заведётся вторая карточка.
          </p>
        ) : null}

        <div className="market-supplier-card__grid">
          <label className={`echat-field${nameInvalid ? ' echat-field--invalid' : ''}`}>
            <span className="echat-field__label">Название поставщика</span>
            <input
              className="echat-field__input"
              value={legalName}
              onChange={(event) => setLegalName(event.target.value)}
            />
            {nameInvalid ? (
              <span className="echat-field__error" role="alert">
                Название не может быть пустым
              </span>
            ) : !supplier.ogrn ? (
              <span className="echat-field__label">
                У карточки нет регистрационного номера, поэтому следующий прайс-лист
                привязывается к ней по названию. После переименования загрузите прайс
                отсюда, кнопкой «Обновить прайс», — иначе заведётся вторая карточка.
              </span>
            ) : null}
          </label>

          <label className={`echat-field${innInvalid ? ' echat-field--invalid' : ''}`}>
            <span className="echat-field__label">ИНН</span>
            <input
              className="echat-field__input"
              inputMode="numeric"
              value={inn}
              placeholder="Необязательно"
              onChange={(event) => setInn(event.target.value)}
            />
            {innInvalid ? (
              <span className="echat-field__error" role="alert">
                ИНН — это 10 цифр у организации или 12 у предпринимателя
              </span>
            ) : null}
          </label>

        </div>

        {/*
          Чекбокс стоит в заголовке того поля, которое он отключает. В соседней колонке
          он читался как отдельная настройка, а связь «отметил — поле погасло» приходилось
          угадывать.
        */}
        <div
          className={`echat-field market-supplier-card__amount${amountInvalid ? ' echat-field--invalid' : ''}`}
        >
          <div className="market-supplier-card__amount-head">
            <label className="echat-field__label" htmlFor="supplier-card-amount">
              Минимальная сумма заказа, ₽
            </label>
            <label className="market-supplier-card__amount-unknown">
              <input
                type="checkbox"
                checked={unknown}
                onChange={(event) => {
                  setUnknown(event.target.checked)
                  // Сумма и отметка — взаимоисключающие ответы, поэтому поле очищается
                  // сразу, а не молча отбрасывается на сервере.
                  if (event.target.checked) setAmount('')
                }}
              />
              неизвестна
            </label>
          </div>
          <input
            id="supplier-card-amount"
            className="echat-field__input"
            inputMode="decimal"
            value={unknown ? '' : amount}
            disabled={unknown}
            placeholder="4000"
            onChange={(event) => setAmount(event.target.value)}
          />
          {amountInvalid ? (
            <span className="echat-field__error" role="alert">
              Сумма должна быть числом не меньше нуля
            </span>
          ) : null}
        </div>

        <h3 className="market-supplier-card__section">Менеджер поставщика</h3>
        {/*
          Пояснение стоит до полей: основание нужно иметь до того, как вписываешь чужие
          персональные данные, а не узнавать о нём, уже заполнив три поля.

          «Не показываем другим клиентам», а не «показываем только вам»: сотрудники
          площадки с ролями admin и operator карточку видят, и обещание «только вам»
          было бы неправдой. Формулировка совпадает с пунктом 7.2 политики.
        */}
        <p className="market-muted market-supplier-card__note">
          Контакты менеджера вносите, если у вас есть на это основание: оператором этих
          персональных данных выступаете вы. Мы храним их зашифрованными и не показываем
          другим клиентам.
        </p>
        <div className="market-supplier-card__grid market-supplier-card__grid--tight">
          <label className="echat-field">
            <span className="echat-field__label">Фамилия, имя, отчество</span>
            <input
              className="echat-field__input"
              value={managerName}
              onChange={(event) => setManagerName(event.target.value)}
            />
          </label>
          <label className="echat-field">
            <span className="echat-field__label">Телефон</span>
            <input
              className="echat-field__input"
              inputMode="tel"
              value={managerPhone}
              onChange={(event) => setManagerPhone(event.target.value)}
            />
          </label>
          <label className="echat-field">
            <span className="echat-field__label">Электронная почта</span>
            <input
              className="echat-field__input"
              inputMode="email"
              value={managerEmail}
              onChange={(event) => setManagerEmail(event.target.value)}
            />
          </label>
        </div>
        {error ? (
          <p className="echat-field__error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="market-paywall-modal__actions">
          <button
            type="button"
            className="echat-btn echat-btn--ghost echat-btn--sm"
            onClick={onDismiss}
            disabled={saving}
          >
            Отмена
          </button>
          <button
            type="button"
            className="echat-btn echat-btn--primary echat-btn--sm"
            onClick={() => void handleSave()}
            disabled={saving}
          >
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  )
}
